import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ChevronRight } from "lucide-react";
import TopNav from "@/components/TopNav";
import { parts } from "@/data/partsList";

const STORAGE_KEY = "checklist-checked-parts";

export default function PartsChecklist() {
  const navigate = useNavigate();
  const [checked, setChecked] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
    } catch {
      /* ignore quota / privacy mode */
    }
  }, [checked]);

  const toggle = (id) =>
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  const total = parts.length;
  const done = parts.filter((p) => checked[p.id]).length;
  const pct = Math.round((done / total) * 100);

  return (
    <div
      className="min-h-screen flex flex-col bg-[var(--bg-base)]"
      data-testid="checklist-page"
    >
      <TopNav />

      <main className="flex-1 w-full mx-auto max-w-3xl px-4 sm:px-6 pt-6 pb-32">
        {/* Heading */}
        <div className="mb-5">
          <h1
            className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight leading-snug"
            data-testid="checklist-title"
          >
            <span className="text-[#d64533]">Before we begin:</span>{" "}
            <span className="text-[var(--text-main)]">
              Tap the checklist cards to mark off the parts you have.
            </span>
          </h1>
        </div>

        <div className="flex items-start">
          {/* Cards */}
          <ul
            className="flex-1 min-w-0 flex flex-col gap-3"
            data-testid="checklist-list"
          >
            {parts.map((p, i) => (
              <PartCard
                key={p.id}
                part={p}
                isChecked={!!checked[p.id]}
                onToggle={() => toggle(p.id)}
                delay={i * 0.02}
              />
            ))}
          </ul>
        </div>
      </main>

      {/* Bottom bar — progress pill while incomplete; once every part is
          checked, the pill is replaced by a small circular arrow button. */}
      <div
        className="pointer-events-none fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center justify-center w-[min(480px,calc(100vw-32px))] px-2"
        data-testid="checklist-bottom-nav"
      >
        {done < total ? (
          <div
            className="pointer-events-none flex-1 min-w-0"
            data-testid="checklist-progress-wrap"
          >
            <div className="relative h-12 rounded-full bg-white border-2 border-[var(--border-heavy)] shadow-[3px_3px_0px_var(--border-heavy)] overflow-hidden">
              <div
                aria-hidden
                className="absolute inset-y-0 left-0 bg-[var(--arcade-red)] transition-[width] duration-500 ease-out"
                style={{ width: `${pct}%` }}
                data-testid="checklist-progress-fill"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <span
                  className="font-heading text-base sm:text-lg font-semibold tabular-nums text-[var(--text-main)]"
                  data-testid="checklist-progress-count"
                >
                  {done}/{total}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => navigate("/discuss")}
            aria-label="Continue to game idea discussion"
            data-testid="checklist-next-btn"
            className="arcade-btn arcade-btn-icon pointer-events-auto"
          >
            <ChevronRight strokeWidth={3} className="h-6 w-6" />
          </button>
        )}
      </div>
    </div>
  );
}

export function PartCard({ part, isChecked, onToggle, delay = 0 }) {
  return (
    <motion.li
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ delay, duration: 0.3 }}
      data-testid={`part-card-${part.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={isChecked}
        data-testid={`part-toggle-${part.id}`}
        className={`relative w-full text-left rounded-2xl border-2 overflow-hidden transition-colors duration-200 press-btn ${
          isChecked
            ? "bg-[#f6c445] border-[var(--border-heavy)] shadow-[3px_3px_0px_var(--border-heavy)]"
            : "bg-[#D9D9D9] border-[var(--border-heavy)]/70 shadow-[3px_3px_0px_var(--border-heavy)]/40"
        }`}
      >
        <div className="flex sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] items-stretch gap-0">
          {/* Image tile — fixed 96×96 px on mobile using flexbox (avoids all
              iOS Safari `aspect-ratio` + CSS-grid quirks). On sm+ screens it
              expands to fill its grid cell. `flex-shrink-0` guarantees the
              tile never shrinks under label pressure. */}
          <div className="flex-shrink-0 m-2 sm:m-3 rounded-xl bg-white border border-[var(--border-heavy)]/20 flex items-center justify-center p-2 sm:p-4 w-24 h-24 sm:w-auto sm:h-auto sm:aspect-square">
            <img
              src={part.img}
              alt={part.label}
              className={`w-auto h-auto object-contain ${
                part.id === "small-spacer"
                  ? "max-w-[65%] max-h-[65%]"
                  : "max-w-full max-h-full"
              }`}
              loading="lazy"
              data-testid={`part-img-${part.id}`}
            />
          </div>

          {/* Label — flex-1 on mobile takes remaining row width, min-w-0 lets
              the container shrink so long strings can wrap without pushing
              the tile around. `overflow-hidden` is a final safety belt. */}
          <div className="flex-1 min-w-0 sm:flex-none flex items-center px-3 sm:px-5 pr-10 sm:pr-14 py-4 overflow-hidden">
            <span
              className={`font-body font-bold leading-snug text-sm sm:text-base [overflow-wrap:normal] [word-break:normal] hyphens-none ${
                isChecked ? "text-white" : "text-[#d64533]"
              }`}
              data-testid={`part-label-${part.id}`}
            >
              {part.label}
            </span>
          </div>
        </div>

        {/* Checkmark badge (visible when checked) */}
        {isChecked && (
          <span
            className="absolute top-3 right-3 flex h-7 w-7 items-center justify-center rounded-full bg-white text-[#d64533] shadow-[2px_2px_0px_var(--border-heavy)]"
            data-testid={`part-check-${part.id}`}
          >
            <Check strokeWidth={4} className="h-4 w-4" />
          </span>
        )}
      </button>
    </motion.li>
  );
}
